/**
 * THE OUTBOX SCREEN — where the four states become visible.
 *
 * The design rule: **every item shows its state, its attempt count, and, if it failed, WHY.** A
 * spinner that never resolves is how the incumbent path lost 26.9% of rows without anyone noticing;
 * "sent / waiting / failed, and here is the reason" is the whole reason this screen exists.
 *
 * `failed` items get a "Try again" button and are never auto-cleared. A technician deciding to discard
 * their own work is fine; the app deciding it for them is data loss with a tidy UI.
 *
 * **Discard is TWO taps, inline.** The first tap swaps the button row for a confirmation that
 * restates the cost in a sentence; the second performs it. Inline rather than a modal because
 * gloves and modals mix badly (a dialog needs a precise dismiss target and traps focus behind
 * it), and focus is MOVED to the confirm button so the swap is announced by focus itself rather
 * than by a live region inserted-with-text — the exact announcement failure the sync report's
 * live region below documents. "Keep it" restores the row and returns focus to Discard.
 *
 * **These strings are hardcoded English on purpose, for the Phase 1 pilot** — a decision, not an
 * oversight. See `ENGLISH_ONLY_PILOT_DECISION` in `src/i18n/index.ts` for what was decided, why
 * (PRD Phase 1 says English only), and what would reopen it.
 */

import { useEffect, useReducer, useRef, useState } from 'react';
import {
  countByState,
  remove,
  retryFailed,
  type OutboxItem,
  type OutboxState,
} from '../core/outbox';
import { flushOnce } from '../core/sync';

const STATE_LABEL: Record<OutboxState, string> = {
  queued: 'waiting',
  'in-flight': 'sending',
  synced: 'sent',
  failed: 'failed',
};

function when(timestamp: number): string {
  return new Date(timestamp).toLocaleString();
}

function dueIn(item: OutboxItem): string | null {
  if (item.state !== 'queued' || item.nextAttemptAt === undefined) {
    return null;
  }
  const seconds = Math.max(0, Math.round((item.nextAttemptAt - Date.now()) / 1000));
  return seconds === 0 ? 'retrying now' : `retrying in ${seconds}s`;
}

export function OutboxPanel({ items }: { items: readonly OutboxItem[] }): JSX.Element {
  const counts = countByState(items);
  const [busy, setBusy] = useState(false);
  const [lastReport, setLastReport] = useState<string | null>(null);

  /**
   * `synced` rows only — the server already has this work, so clearing the local record
   * destroys nothing (the same distinction the per-row `Clear` button's comment draws
   * against `Discard`). Computed from the CURRENT items list at click time, never from a
   * cached selection, so a row that flips state between render and tap cannot be swept up.
   */
  const clearAllSent = async (): Promise<void> => {
    for (const item of items) {
      if (item.state === 'synced') {
        await remove(item.id);
      }
    }
  };

  const syncNow = async (): Promise<void> => {
    setBusy(true);
    try {
      const report = await flushOnce();
      // The report is shown verbatim-ish, including "skipped". "Nothing happened and here is why" is
      // information; a button that silently does nothing is not.
      setLastReport(
        report.skipped !== undefined
          ? `Nothing sent (${report.skipped.replace(/-/g, ' ')}).`
          : `Tried ${report.attempted}: ${report.synced} sent, ${report.retrying} will retry, ${report.failed} failed.` +
              (report.notes.length > 0 ? ` ${report.notes[0]}` : ''),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <section>
      <ul className="counts">
        <li>
          waiting <strong>{counts.queued}</strong>
        </li>
        <li>
          sending <strong>{counts.inFlight}</strong>
        </li>
        <li>
          sent <strong>{counts.synced}</strong>
        </li>
        <li>
          failed <strong>{counts.failed}</strong>
        </li>
      </ul>

      <button className="button" onClick={() => void syncNow()} disabled={busy} aria-busy={busy}>
        {busy ? 'Sending…' : 'Send now'}
      </button>
      {/*
        The live region is rendered UNCONDITIONALLY and starts empty.

        A `role="status"` element that is inserted into the document at the same moment it gains its
        text is frequently not announced at all — several screen readers only watch regions that were
        already present. Mounting it empty and filling it later is the difference between a blind
        technician hearing "Tried 3: 3 sent" and hearing nothing after pressing the only button on the
        screen.
      */}
      <p className="muted" role="status" aria-live="polite">
        {lastReport ?? ''}
      </p>

      {items.length === 0 ? (
        <p className="muted">Nothing in the outbox.</p>
      ) : (
        <>
          <h2 className="section-title">Items</h2>
          {counts.synced > 0 ? (
            /*
              Bulk clear for sent rows only. Without it, each finished job costs one tap per row
              to tidy — and rows nobody clears bury the failed one that needs attention. No
              confirmation step, deliberately: unlike Discard this destroys nothing, and adding
              a confirm here would teach technicians that confirmations are noise.
            */
            <p>
              <button
                type="button"
                className="button"
                onClick={() => void clearAllSent()}
                aria-label={`Clear all sent items — removes ${counts.synced} ${counts.synced === 1 ? 'record' : 'records'} of work the server already has`}
              >
                Clear all sent ({counts.synced})
              </button>
            </p>
          ) : null}
          {[...items]
            // Newest first for reading; the FLUSHER still drains oldest-first (see outbox.ts).
            .sort((a, b) => b.createdAt - a.createdAt)
            .map((item) => (
              <OutboxRow key={item.id} item={item} />
            ))}
        </>
      )}
    </section>
  );
}

function OutboxRow({ item }: { item: OutboxItem }): JSX.Element {
  /**
   * D2 — the countdown TICKS. `dueIn` is a pure render-time computation, so without this a
   * row said "retrying in 34s" forever: honest at paint time, stale one second later, and a
   * frozen countdown reads as a hung app. The interval exists ONLY while there is a live
   * countdown to show (queued, with a future attempt) and is torn down the moment the state
   * changes — a panel of sent rows runs zero timers.
   */
  const [, tick] = useReducer((n: number) => n + 1, 0);
  useEffect(() => {
    if (item.state !== 'queued' || item.nextAttemptAt === undefined) {
      return undefined;
    }
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [item.state, item.nextAttemptAt]);

  /**
   * D1 — Discard is a decision, so it is TWO taps. See the file header for why this is
   * inline rather than a modal. Focus is moved by the two effects below (to the confirm
   * button on entry; back to Discard on "Keep it") because focus movement is the one
   * announcement mechanism that works identically everywhere — `wasConfirming` exists so
   * the second effect cannot steal focus on first mount.
   */
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);
  const confirmButton = useRef<HTMLButtonElement | null>(null);
  const discardButton = useRef<HTMLButtonElement | null>(null);
  const wasConfirming = useRef(false);
  useEffect(() => {
    if (confirmingDiscard) {
      wasConfirming.current = true;
      confirmButton.current?.focus();
    } else if (wasConfirming.current) {
      wasConfirming.current = false;
      discardButton.current?.focus();
    }
  }, [confirmingDiscard]);
  const confirmTextId = `discard-confirm-${item.id}`;

  const due = dueIn(item);
  return (
    <article className="card">
      <div className="card__row">
        <strong>{item.kind}</strong>
        <span className={`state state--${item.state}`}>{STATE_LABEL[item.state]}</span>
      </div>
      <p className="muted">
        created {when(item.createdAt)} · {item.attempts} {item.attempts === 1 ? 'attempt' : 'attempts'}
        {due !== null ? ` · ${due}` : ''}
      </p>
      {/*
        The idempotency key is shown. It is not a secret — it is the client's own half, the server
        echoes it back, and it is the one string that lets support correlate "the technician says it
        vanished" with a row in the ledger. It is deliberately NOT the server-namespaced form, which
        the client never learns.
      */}
      <p className="mono muted">key {item.idempotencyKey}</p>
      {item.lastError !== undefined ? (
        <p className="muted">Last problem: {item.lastError}</p>
      ) : null}
      {item.state === 'failed' && !confirmingDiscard ? (
        <>
          {/*
            A raw `lastError` says what failed. It does not say what to do, and the two available
            actions are not equivalent — one is safe and one is irreversible. Saying so in one sentence
            is what makes "Discard" a decision rather than the button next to the other button.
          */}
          <p>
            <strong>What to do:</strong> press <strong>Try again</strong> — this is safe and re-uses the
            same key, so it cannot create a second record. Only <strong>Discard</strong> if you are
            willing to lose this submission and re-do the job; nothing recovers it afterwards.
          </p>
          <div className="fx-row">
            <button
              className="button"
              onClick={() => void retryFailed(item.id)}
              aria-label={`Try again — ${item.kind}, key ${item.idempotencyKey}`}
            >
              Try again
            </button>
            {/*
              First tap OPENS the confirmation; nothing is removed here. A one-tap permanent
              delete next to the safe button is exactly the gloved-thumb slip this screen's
              own copy warns about.
            */}
            <button
              ref={discardButton}
              className="button button--danger"
              onClick={() => setConfirmingDiscard(true)}
              aria-label={`Discard — ${item.kind}, key ${item.idempotencyKey}`}
            >
              Discard
            </button>
          </div>
        </>
      ) : null}
      {item.state === 'failed' && confirmingDiscard ? (
        <>
          <p id={confirmTextId}>
            <strong>This cannot be undone.</strong> Discarding permanently deletes this submission
            from the phone. Nothing recovers it, and the job would need doing again.
          </p>
          <div className="fx-row">
            <button
              ref={confirmButton}
              className="button button--danger"
              onClick={() => void remove(item.id)}
              // The sentence above is part of this button's description, so a screen reader
              // that lands here (focus is moved on entry) hears the cost before the action.
              aria-describedby={confirmTextId}
              aria-label={`Yes, discard permanently — ${item.kind}, key ${item.idempotencyKey}`}
            >
              Yes, discard permanently
            </button>
            <button
              className="button"
              onClick={() => setConfirmingDiscard(false)}
              aria-label={`Keep it — ${item.kind} stays in the outbox`}
            >
              Keep it
            </button>
          </div>
        </>
      ) : null}
      {item.state === 'synced' ? (
        <p>
          {/*
            `Clear` on a `synced` row removes a local record of work the server already has, so unlike
            `Discard` above it destroys nothing. The labels are deliberately different words for that
            reason; the `aria-label` says which row, because a list of identical "Clear" buttons is
            unusable with a screen reader.
          */}
          <button
            className="button"
            onClick={() => void remove(item.id)}
            aria-label={`Clear sent item — ${item.kind}, key ${item.idempotencyKey}`}
          >
            Clear
          </button>
        </p>
      ) : null}
    </article>
  );
}
